import React, { memo, useEffect, FC, BaseSyntheticEvent, useState } from 'react'

import { useMutation } from '@apollo/client'
import { useController, useForm, useFormContext } from 'react-hook-form'

import { EMAIL_PATTERN, FORGOT_PASSWORD_CUSTOMER } from 'Const'
import { SignUpStates } from 'Hooks/checkout'
import useEvent from 'Hooks/useEvent'
import useMounted from 'Hooks/useMounted'
import { noty } from 'Utils'

import ErrorMessage from 'Components/ErrorMessage'
import PhoneInput from 'Components/PhoneInput'

import FormErrors from '../../FormErrors'

interface Props {
  closeModal(): void
}

const ForgotPasswordModal: FC<Props> = ({ closeModal }) => {
  const [state, setState] = useState<SignUpStates>()
  const { locationCountryIsoCodes } = window.VARIABLES
  const defaultCountry = locationCountryIsoCodes[0] || 'US'
  const isMounted = useMounted()

  const { watch } = useFormContext()
  const defaultValues = watch(['email', 'phone'])

  const { register, handleSubmit, setError, control, errors } = useForm({
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    defaultValues,
  })
  const { field: phoneField } = useController({
    name: 'phone',
    control,
    rules: {
      required: {
        value: state === SignUpStates.MULTIPLE,
        message: 'Phone number is required',
      },
    },
  })

  const [forgotPasswordCustomer, { error }] = useMutation(FORGOT_PASSWORD_CUSTOMER)

  const onSubmit = useEvent(
    (e?: BaseSyntheticEvent) => {
      e?.stopPropagation()
      const submit = handleSubmit((payload) => {
        void forgotPasswordCustomer({ variables: payload }).then(({ data }) => {
          if (!isMounted()) return
          setState(data.forgotPasswordCustomer.state as SignUpStates)

          if (data.forgotPasswordCustomer.success) {
            noty(
              'You will receive an email with instructions on how to reset your password in a few minutes.',
              'success',
            )
            closeModal()
          }
        })
      }, console.error)

      void submit(e)
    },
    [closeModal, forgotPasswordCustomer],
  )

  useEffect(() => {
    if (error) {
      error.graphQLErrors.forEach((error) => {
        if (error.extensions?.fieldName) {
          setError(error.extensions.fieldName as string, { message: error.message })
        }
      })
    }
  }, [error, setError])

  return (
    <>
      <div className="modal fade show" style={{ display: 'block' }}>
        <div className="modal-dialog modal-dialog-centered">
          <form className="modal-content" onSubmit={onSubmit}>
            <div className="modal-header align-items-center">
              <h4 className="modal-title">Password Reset</h4>
              <button aria-hidden="true" className="close pull-right" type="button" onClick={closeModal}>
                ×
              </button>
            </div>

            <div className="modal-body">
              <div className="form-group row align-items-center">
                <label className="col-sm-2 mb-0" htmlFor="customer-email">
                  Email
                </label>
                <div className="col-sm-10">
                  <input
                    ref={register({
                      required: 'Email is required',
                      pattern: {
                        value: EMAIL_PATTERN,
                        message: 'Email incorrect',
                      },
                    })}
                    required
                    className="form-control mb-0"
                    id="customer-email"
                    name="email"
                    placeholder="Enter your email..."
                    type="email"
                  />
                  <ErrorMessage as={<FormErrors className="m-0" />} errors={errors} name="email" />
                </div>
              </div>

              <div className="form-group row align-items-center" hidden={state !== SignUpStates.MULTIPLE}>
                <label className="col-sm-2 mb-0" htmlFor="customer-email">
                  Phone
                </label>
                <div className="col-sm-10">
                  <PhoneInput
                    {...phoneField}
                    required
                    addInternationalOption={false}
                    autoComplete="tel"
                    countryOptionsOrder={locationCountryIsoCodes}
                    defaultCountry={defaultCountry}
                    id="customer-phone"
                    numberInputProps={{ className: 'form-control' }}
                    placeholder="Enter your phone number"
                  />
                  <ErrorMessage as={<FormErrors className="m-0" />} errors={errors} name="phone" />
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn btn-accent" type="submit">
                Reset password
              </button>
            </div>
          </form>
        </div>
      </div>
      <div className="modal-backdrop fade show" />
    </>
  )
}

export default memo(ForgotPasswordModal)
